/**
 * format.ts — number formatting for the results panel and the note (P1 US-114, FR-27).
 *
 * Ported from oracle lines 872–877. Four branches, and each one is load-bearing.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * `fmt` IS LOCALE-INDEPENDENT, IN A FRENCH APPLICATION, ON PURPOSE
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * Every label in this tool is French and every number it prints uses a decimal **point**. That
 * looks like an oversight and is not one worth "fixing": `toFixed` and `toExponential` are locale-
 * independent by specification, so the output is identical on a French, German and US machine.
 * Switching to `toLocaleString` would make the note de calcul — a document an engineer signs —
 * render differently depending on the browser's locale, and US-116 byte-compares that document
 * against oracle-generated output.
 *
 * The single locale-aware call in the whole application is the note's date, and it is deliberate
 * (`toLocaleDateString('fr-FR')`, US-116).
 */

/**
 * Format an engineering value.
 *
 * The branches, in the order the oracle tests them:
 *
 *  1. **Non-finite → `'-'`.** `null`, `undefined`, `NaN`, `±Infinity` all render as a dash. The
 *     engine genuinely emits these — a check that could not be evaluated has a null ratio, and
 *     `DEVIATIONS.md` D-001 records that non-finite values reach the value bag — so this is the
 *     normal path for a real result, not an error case.
 *  2. **Very large or very small → exponential**, at `|x| >= 1e5` or `0 < |x| < 1e-3`. Section
 *     properties make this routine: an `Iy` of 8.36e7 mm⁴ printed in full is unreadable.
 *     `toExponential(3)` then `.replace('e+', ' E')` gives `8.356 E7` — the oracle's own spelling,
 *     with a space and a capital E, and note that only `e+` is replaced, so a negative exponent
 *     stays as `e-`.
 *  3. **Otherwise `toFixed(n)`**, with a narrow trailing-zero strip. Default 2 decimals.
 *
 * The strip is narrower than it looks, and worth reading twice. `/\.?0+$/` matches a run of zeros
 * with an optional decimal point in front, and the replacer **keeps the match unless it starts with
 * that point**. So it removes a fractional part only when the fractional part is entirely zeros:
 *
 *     1.00     -> 1          (match '.00' starts with '.', dropped)
 *     1234.50  -> 1234.50    (match '0' does not, kept)
 *     100      -> 100        (match '00' does not, kept)
 *
 * The guard is what stops the regex eating a round integer's zeros and turning 100 into 1. The
 * middle case is the one that surprises people, including the author of this file's first test.
 */
export function fmt(x: unknown, n = 2): string {
  if (x === null || x === undefined || typeof x !== 'number' || !Number.isFinite(x)) return '-';

  if (Math.abs(x) >= 1e5 || (Math.abs(x) < 1e-3 && x !== 0)) {
    return x.toExponential(3).replace('e+', ' E');
  }

  return x.toFixed(n).replace(/\.?0+$/, (m) => (m.indexOf('.') === 0 ? '' : m));
}

/**
 * Format a ratio for display: always three decimals, never through `fmt`.
 *
 * The oracle bypasses `fmt` for every utilisation ratio — the banner (line 1069), the check table
 * (887), the optimiser table (1134) and the note's conclusion (1182) all call `toFixed(3)`
 * directly. That is not an inconsistency to tidy up: a ratio of exactly 1 must read `1.000`, not
 * `1`, because the whole question an engineer is asking is how close to 1 it is, and `fmt` would
 * strip the very digits that answer it.
 */
export function ratio3(value: number): string {
  return value.toFixed(3);
}
